// ---------------------------------------------------------------------------
// Conversión entre Fecha Vencimiento (fecha_exp, formato 'AAAA-MM-DD') y
// Fecha Juliana (fecha_venc, formato mes-año)
//
// Módulo compartido por ModalRevisarVencimiento (Modales.jsx) y por
// RowVencimiento (RowCaptura.jsx), de modo que ambos mantengan los dos campos
// sincronizados con exactamente las mismas reglas.
// ---------------------------------------------------------------------------

// Formato con el que se escribe la Fecha Juliana
// Opciones: 'MM-AAAA' (ej. 12-2029) | 'MM-AA' (ej. 12-29) | 'MMAA' (ej. 1229) | 'MM/AA' | 'MM/AAAA'
export const FORMATO_JULIANO = 'MM-AAAA'

// Día que se asigna a la Fecha Vencimiento cuando sólo se conoce mes y año.
// Se usa 'ultimo' (último día del mes) porque la Fecha Juliana indica el mes/año
// en que expira el producto: éste sigue vigente hasta el último día de ese mes.
// Aplica al apartado Vencimiento de las auditorías (modal Revisar Vencimiento y
// fila de la tabla de vencimientos). El backend resuelve la Fecha Juliana con
// este mismo criterio (último día del mes): la importación de capturas en
// ResultadoAuditoriaService::_getDateExp() y el alta manual / pegado desde Excel en
// ResultadoAuditoriaController::api_nueva_captura_venc -> FechaHelper::formato().
// Opciones: 'primero' (día 1) | 'ultimo' (fin de mes)
export const DIA_VENCIMIENTO_JULIANO = 'ultimo'

// Cantidad de dígitos esperada según el formato configurado (mes + año)
export const DIGITOS_JULIANO = {
    'MM-AAAA': 6,
    'MM/AAAA': 6,
    'MMAAAA': 6,
    'MM-AA': 4,
    'MM/AA': 4,
    'MMAA': 4
}

export const dosDigitos = (numero)=>
    String(numero).length < 2? '0' + numero : String(numero)

// 'AAAA-MM-DD' => {anio, mes, dia} | null si la fecha no es válida o está incompleta
export const leerFecha = (valor)=>{
    const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(valor == null? '' : valor).trim())
    if(!partes){
        return null
    }
    const fecha = {
        anio: parseInt(partes[1], 10),
        mes: parseInt(partes[2], 10),
        dia: parseInt(partes[3], 10)
    }
    const valida = fecha.anio > 0 && fecha.mes >= 1 && fecha.mes <= 12 && fecha.dia >= 1 && fecha.dia <= 31
    return valida? fecha : null
}

// 'MM-AAAA' | 'MMAA' | 'MM-AA' | 'MM/AA' | 'MM/AAAA' => {anio, mes} | null
export const leerJuliano = (valor)=>{
    const digitos = String(valor == null? '' : valor).replace(/\D/g, '')
    //Sólo se sincroniza cuando la captura está completa (evita valores parciales como '12-20')
    const digitosEsperados = DIGITOS_JULIANO[FORMATO_JULIANO] || 4
    if(digitos.length !== digitosEsperados){
        return null
    }
    const mes = parseInt(digitos.substring(0, 2), 10)
    const anio = parseInt(digitos.substring(2), 10)
    if(!(mes >= 1 && mes <= 12) || isNaN(anio)){
        return null
    }
    //Con 4 dígitos se asume que el año es de 2 dígitos (2000 + AA)
    return { mes: mes, anio: digitosEsperados === 4? 2000 + anio : anio }
}

export const escribirJuliano = (mes, anio)=>{
    switch(FORMATO_JULIANO){
        case 'MM-AAAA':
            return dosDigitos(mes) + '-' + anio
        case 'MM-AA':
            return dosDigitos(mes) + '-' + dosDigitos(anio % 100)
        case 'MMAAAA':
            return dosDigitos(mes) + anio
        case 'MM/AA':
            return dosDigitos(mes) + '/' + dosDigitos(anio % 100)
        case 'MM/AAAA':
            return dosDigitos(mes) + '/' + anio
        default:
            return dosDigitos(mes) + dosDigitos(anio % 100)
    }
}

// {mes, anio} => 'AAAA-MM-DD' (usa el día configurado en DIA_VENCIMIENTO_JULIANO:
// día 1 o último día del mes)
export const escribirFecha = (mes, anio)=>{
    const dia = DIA_VENCIMIENTO_JULIANO === 'primero'? 1 : new Date(anio, mes, 0).getDate()
    return anio + '-' + dosDigitos(mes) + '-' + dosDigitos(dia)
}

// Expresión regular de la Fecha Juliana según el formato configurado. Se exige la
// forma completa (mes + separador + año) para que el usuario no pueda guardar un
// texto que no sea interpretable como fecha juliana.
const REGEX_JULIANO = {
    'MM-AAAA': /^\d{2}-\d{4}$/,
    'MM-AA': /^\d{2}-\d{2}$/,
    'MMAAAA': /^\d{6}$/,
    'MM/AA': /^\d{2}\/\d{2}$/,
    'MM/AAAA': /^\d{2}\/\d{4}$/
}
const REGEX_JULIANO_FORMATO = REGEX_JULIANO[FORMATO_JULIANO] || /^\d+$/

// Mensaje corto que se muestra bajo el input de Fecha Juliana cuando el texto
// escrito a mano no es una fecha interpretable (mes + año).
// La Fecha Vencimiento no se valida: es un input type="date", por lo que el
// navegador ya restringe las fechas posibles a su rango y no admite textos.
export const MENSAJE_JULIANO_INVALIDO = 'Fecha ingresada fuera de rango'

// Verificador de la Fecha Juliana escrita a mano en el input (mes + año).
// Devuelve el mensaje de error si la fecha no es válida, o null si lo es.
export const validarFechaJuliano = (valor)=>{
    const texto = String(valor == null? '' : valor).trim()
    //Sin texto, con un formato distinto al configurado (FORMATO_JULIANO) o con
    //mes/año fuera de rango: la captura no es interpretable como fecha juliana
    if(texto === '' || !REGEX_JULIANO_FORMATO.test(texto) || !leerJuliano(texto)){
        return MENSAJE_JULIANO_INVALIDO
    }
    return null
}

// Campos a actualizar en la captura cuando se corrige la Fecha Vencimiento
// (date, 'AAAA-MM-DD'). La Fecha Juliana se recalcula en simultáneo.
export const sincronizarDesdeFechaExp = (valor)=>{
    let actualizado = {
        fecha_exp: valor,
        editado: 1
    }
    const fecha = leerFecha(valor)
    if(fecha){
        actualizado.fecha_venc = escribirJuliano(fecha.mes, fecha.anio)
    }
    return actualizado
}

// Campos a actualizar en la captura cuando se corrige la Fecha Juliana
// (fecha_venc, mes + año). La Fecha Vencimiento se recalcula en simultáneo con
// el día configurado en DIA_VENCIMIENTO_JULIANO (último día del mes), siempre y
// cuando la juliano esté completa.
export const sincronizarDesdeFechaVenc = (valor)=>{
    let actualizado = {
        fecha_venc: valor,
        editado: 1
    }
    const juliano = leerJuliano(valor)
    if(juliano){
        //Siempre se recalcula la fecha (aunque el mes/año coincida con el que ya
        //tenía), para que la vista y la base de datos guarden el mismo día: el
        //configurado en DIA_VENCIMIENTO_JULIANO (último día del mes)
        actualizado.fecha_exp = escribirFecha(juliano.mes, juliano.anio)
    }
    return actualizado
}



// WEBPACK FOOTER //
// ./frontEnd/auditorias/revision/fechasVencimiento.js